import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import {
  ArrowLeftRight,
  Boxes,
  Check,
  HelpCircle,
  Plus,
  Radio,
  Save,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Eyebrow } from "@/components/dravian/eyebrow";
import { WorkspaceShell } from "@/components/dravian/workspace-shell";
import {
  ontologies as ontologiesApi,
  type ApiOntology,
  type OntologyEntityType,
  type OntologyRelationshipType,
  type OntologySignalType,
} from "@/lib/api";
import { useWorkspaces } from "@/store/workspaces";

export const Route = createFileRoute("/$workspace/ontologies")({
  head: () => ({ meta: [{ title: "Ontologies — Dravian Horizon" }] }),
  component: OntologyPage,
});

const FRIENDLY_NAME: Record<string, string> = {
  colombian_conflict: "Conflict Ontology",
  financial: "Financial Ontology",
  supply_chain: "Supply Chain Ontology",
  healthcare: "Healthcare Ontology",
};

const ATTR_TYPES = ["string", "integer", "float", "boolean", "date"];

type SelectedTab = "entity" | "relationship" | "signal";

function OntologyPage() {
  const { workspace: id } = useParams({ from: "/$workspace/ontologies" });
  const { getById } = useWorkspaces();
  const ws = getById(id);
  const qc = useQueryClient();

  const ontsQ = useQuery({
    queryKey: ["ontologies"],
    queryFn: ontologiesApi.list,
  });

  const remote: ApiOntology | undefined = useMemo(
    () => (ontsQ.data ?? []).find((o) => o.id === ws?.ontology_id),
    [ontsQ.data, ws?.ontology_id],
  );

  const usageQ = useQuery({
    queryKey: ["ontology-usage", remote?.id],
    queryFn: () => ontologiesApi.usage(remote!.id),
    enabled: Boolean(remote?.id),
  });

  // Local working copy. Reset whenever the remote changes.
  const [draft, setDraft] = useState<ApiOntology | null>(null);
  useEffect(() => {
    if (remote) setDraft(structuredClone(remote));
  }, [remote]);

  const [tab, setTab] = useState<SelectedTab>("entity");
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const isDirty = useMemo(() => {
    if (!draft || !remote) return false;
    return JSON.stringify(draft) !== JSON.stringify(remote);
  }, [draft, remote]);

  const saveMut = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error("no draft");
      return ontologiesApi.update(draft.id, {
        name: draft.name,
        version: draft.version,
        description: draft.description ?? null,
        entity_types: draft.entity_types,
        relationship_types: draft.relationship_types,
        signal_types: draft.signal_types,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ontologies"] }),
  });

  const discard = () => {
    if (remote) setDraft(structuredClone(remote));
  };

  if (ontsQ.isLoading) {
    return (
      <WorkspaceShell>
        <div className="p-5 text-[12px] text-muted-foreground">Loading ontology…</div>
      </WorkspaceShell>
    );
  }

  if (!ws?.ontology_id || !remote || !draft) {
    return (
      <WorkspaceShell>
        <div className="p-5">
          <div className="label-eyebrow mb-1">Ontology</div>
          <h2 className="text-[20px] font-semibold mb-3">No ontology assigned</h2>
          <p className="text-[12px] text-muted-foreground max-w-prose">
            This workspace runs without a schema. Assign one from settings to enable
            type validation and richer chat understanding.
          </p>
        </div>
      </WorkspaceShell>
    );
  }

  const niceName =
    FRIENDLY_NAME[draft.name] ??
    draft.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) +
      " Ontology";

  const selectedEntity =
    tab === "entity" ? draft.entity_types.find((e) => e.name === selectedName) : undefined;
  const selectedRel =
    tab === "relationship"
      ? draft.relationship_types.find((r) => r.name === selectedName)
      : undefined;
  const selectedSig =
    tab === "signal" ? draft.signal_types.find((s) => s.name === selectedName) : undefined;

  return (
    <WorkspaceShell>
      <div className="p-5 max-w-[1300px] mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-eyebrow mb-1">Ontology</div>
            <div className="flex items-baseline gap-3">
              <h2 className="text-[20px] font-semibold">{niceName}</h2>
              <span className="text-[11px] text-muted-foreground font-mono">
                v{draft.version}
              </span>
              {draft.source === "user" && (
                <span className="text-[10px] uppercase tracking-wider text-primary border border-primary/30 bg-primary/10 px-1.5 py-0.5 rounded">
                  edited
                </span>
              )}
            </div>
            <p className="text-[12px] text-muted-foreground mt-1 max-w-prose">
              Defines what entity types, relationships, and signals are valid here.
              The chat and ingestion pipeline read this schema and reject anything
              outside of it. Edit a type to see what each parameter does.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isDirty && (
              <>
                <span className="text-[11px] text-risk-medium">Unsaved changes</span>
                <button
                  onClick={discard}
                  className="rounded border border-border bg-surface px-2.5 py-1.5 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Discard
                </button>
                <button
                  onClick={() => saveMut.mutate()}
                  disabled={saveMut.isPending}
                  className="inline-flex items-center gap-1.5 rounded bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-semibold disabled:opacity-60"
                >
                  <Save className="h-3 w-3" />
                  {saveMut.isPending ? "Saving…" : "Save"}
                </button>
              </>
            )}
          </div>
        </div>

        {saveMut.isError && (
          <div className="panel border-risk-high/30 bg-risk-high/10 p-3 text-[12px] text-risk-high">
            Save failed:{" "}
            {saveMut.error instanceof Error ? saveMut.error.message : "unknown error"}
          </div>
        )}

        {usageQ.data && usageQ.data.workspace_count > 1 && (
          <div className="panel p-3 text-[11px] text-muted-foreground border-primary/30 bg-primary/5 flex items-center gap-2">
            <HelpCircle className="h-3.5 w-3.5 text-primary shrink-0" />
            This ontology is shared with {usageQ.data.workspace_count - 1} other
            workspace{usageQ.data.workspace_count - 1 === 1 ? "" : "s"}. Edits affect
            all of them.
          </div>
        )}

        <ParameterPrimer />

        {/* Two-column editor: tab + list on left, detail on right */}
        <div className="grid grid-cols-[360px_minmax(0,1fr)] gap-3">
          <div className="panel p-3 space-y-3">
            <div className="flex items-center gap-1 rounded-md bg-surface-elevated/40 p-1">
              <TabButton
                active={tab === "entity"}
                onClick={() => {
                  setTab("entity");
                  setSelectedName(draft.entity_types[0]?.name ?? null);
                }}
                icon={<Boxes className="h-3 w-3" />}
                label="Entities"
                count={draft.entity_types.length}
              />
              <TabButton
                active={tab === "relationship"}
                onClick={() => {
                  setTab("relationship");
                  setSelectedName(draft.relationship_types[0]?.name ?? null);
                }}
                icon={<ArrowLeftRight className="h-3 w-3" />}
                label="Relations"
                count={draft.relationship_types.length}
              />
              <TabButton
                active={tab === "signal"}
                onClick={() => {
                  setTab("signal");
                  setSelectedName(draft.signal_types[0]?.name ?? null);
                }}
                icon={<Radio className="h-3 w-3" />}
                label="Signals"
                count={draft.signal_types.length}
              />
            </div>

            {tab === "entity" && (
              <TypeList
                items={draft.entity_types.map((e) => ({
                  name: e.name,
                  sub:
                    Object.keys(e.attribute_schema).length === 0
                      ? "no attributes"
                      : `${Object.keys(e.attribute_schema).length} attributes`,
                }))}
                selected={selectedName}
                onSelect={setSelectedName}
                onAdd={() => {
                  const next = uniqueName("new_type", draft.entity_types.map((e) => e.name));
                  setDraft({
                    ...draft,
                    entity_types: [
                      ...draft.entity_types,
                      {
                        name: next,
                        description: "",
                        example: "",
                        attribute_schema: {},
                      },
                    ],
                  });
                  setSelectedName(next);
                }}
                addLabel="Add entity type"
              />
            )}

            {tab === "relationship" && (
              <TypeList
                items={draft.relationship_types.map((r) => ({
                  name: r.name,
                  sub: `${r.source_types.join("|")} → ${r.target_types.join("|")}`,
                }))}
                selected={selectedName}
                onSelect={setSelectedName}
                onAdd={() => {
                  const firstType = draft.entity_types[0]?.name;
                  if (!firstType) {
                    alert("Add an entity type first — relationships connect them.");
                    return;
                  }
                  const next = uniqueName(
                    "new_relation",
                    draft.relationship_types.map((r) => r.name),
                  );
                  setDraft({
                    ...draft,
                    relationship_types: [
                      ...draft.relationship_types,
                      {
                        name: next,
                        description: "",
                        example: "",
                        source_types: [firstType],
                        target_types: [firstType],
                      },
                    ],
                  });
                  setSelectedName(next);
                }}
                addLabel="Add relationship type"
              />
            )}

            {tab === "signal" && (
              <TypeList
                items={draft.signal_types.map((s) => ({
                  name: s.name,
                  sub: `${s.related_entity_types.length} related types`,
                }))}
                selected={selectedName}
                onSelect={setSelectedName}
                onAdd={() => {
                  const next = uniqueName(
                    "new_signal",
                    draft.signal_types.map((s) => s.name),
                  );
                  setDraft({
                    ...draft,
                    signal_types: [
                      ...draft.signal_types,
                      {
                        name: next,
                        description: "",
                        example: "",
                        related_entity_types: [],
                      },
                    ],
                  });
                  setSelectedName(next);
                }}
                addLabel="Add signal type"
              />
            )}
          </div>

          <div className="panel p-4">
            {tab === "entity" && selectedEntity && (
              <EntityEditor
                value={selectedEntity}
                onChange={(next) => {
                  setDraft({
                    ...draft,
                    entity_types: draft.entity_types.map((e) =>
                      e.name === selectedEntity.name ? next : e,
                    ),
                  });
                  if (next.name !== selectedEntity.name) setSelectedName(next.name);
                }}
                onDelete={() => {
                  setDraft({
                    ...draft,
                    entity_types: draft.entity_types.filter(
                      (e) => e.name !== selectedEntity.name,
                    ),
                    // also strip references to it from relationships and signals
                    relationship_types: draft.relationship_types.map((r) => ({
                      ...r,
                      source_types: r.source_types.filter((s) => s !== selectedEntity.name),
                      target_types: r.target_types.filter((s) => s !== selectedEntity.name),
                    })),
                    signal_types: draft.signal_types.map((s) => ({
                      ...s,
                      related_entity_types: s.related_entity_types.filter(
                        (t) => t !== selectedEntity.name,
                      ),
                    })),
                  });
                  setSelectedName(null);
                }}
                referencedBy={[
                  ...draft.relationship_types
                    .filter(
                      (r) =>
                        r.source_types.includes(selectedEntity.name) ||
                        r.target_types.includes(selectedEntity.name),
                    )
                    .map((r) => `relationship "${r.name}"`),
                  ...draft.signal_types
                    .filter((s) =>
                      s.related_entity_types.includes(selectedEntity.name),
                    )
                    .map((s) => `signal "${s.name}"`),
                ]}
              />
            )}
            {tab === "relationship" && selectedRel && (
              <RelationshipEditor
                value={selectedRel}
                entityTypes={draft.entity_types.map((e) => e.name)}
                onChange={(next) => {
                  setDraft({
                    ...draft,
                    relationship_types: draft.relationship_types.map((r) =>
                      r.name === selectedRel.name ? next : r,
                    ),
                  });
                  if (next.name !== selectedRel.name) setSelectedName(next.name);
                }}
                onDelete={() => {
                  setDraft({
                    ...draft,
                    relationship_types: draft.relationship_types.filter(
                      (r) => r.name !== selectedRel.name,
                    ),
                  });
                  setSelectedName(null);
                }}
              />
            )}
            {tab === "signal" && selectedSig && (
              <SignalEditor
                value={selectedSig}
                entityTypes={draft.entity_types.map((e) => e.name)}
                onChange={(next) => {
                  setDraft({
                    ...draft,
                    signal_types: draft.signal_types.map((s) =>
                      s.name === selectedSig.name ? next : s,
                    ),
                  });
                  if (next.name !== selectedSig.name) setSelectedName(next.name);
                }}
                onDelete={() => {
                  setDraft({
                    ...draft,
                    signal_types: draft.signal_types.filter((s) => s.name !== selectedSig.name),
                  });
                  setSelectedName(null);
                }}
              />
            )}
            {!selectedEntity && !selectedRel && !selectedSig && (
              <div className="text-center text-[12px] text-muted-foreground py-12">
                Pick a type from the list, or add a new one.
              </div>
            )}
          </div>
        </div>
      </div>
    </WorkspaceShell>
  );
}

// ---------- Reusable subcomponents ----------

function ParameterPrimer() {
  return (
    <div className="panel p-3 grid sm:grid-cols-3 gap-3 text-[11px] text-muted-foreground">
      <PrimerCell
        icon={<Boxes className="h-3.5 w-3.5 text-primary" />}
        title="Entity types"
        body="The nouns. Things that exist and have identity (a counterparty, a supplier, a person, a hospital). Each one defines a list of attributes you expect to track."
      />
      <PrimerCell
        icon={<ArrowLeftRight className="h-3.5 w-3.5 text-primary" />}
        title="Relationships"
        body="The verbs between entities. Each relationship declares which entity types can be on its left ('source') and right ('target') — guardrails so the chat doesn't draw nonsense edges."
      />
      <PrimerCell
        icon={<Radio className="h-3.5 w-3.5 text-primary" />}
        title="Signals"
        body="The events. Time-stamped things that happen to entities — news, alerts, transactions. Each signal type declares which entities it can attach to."
      />
    </div>
  );
}

function PrimerCell({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5">{icon}</div>
      <div>
        <div className="text-foreground/95 text-[11px] font-semibold mb-0.5">{title}</div>
        <div className="leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded px-2 py-1.5 text-[11px] font-medium transition ${
        active
          ? "bg-primary/15 text-primary"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      {label}
      <span className="font-mono text-[10px] text-muted-foreground">{count}</span>
    </button>
  );
}

function TypeList({
  items,
  selected,
  onSelect,
  onAdd,
  addLabel,
}: {
  items: { name: string; sub: string }[];
  selected: string | null;
  onSelect: (name: string) => void;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="space-y-0.5 max-h-[60vh] overflow-y-auto scrollbar-thin">
        {items.map((it) => (
          <button
            key={it.name}
            onClick={() => onSelect(it.name)}
            className={`w-full text-left rounded px-2.5 py-1.5 transition ${
              selected === it.name
                ? "bg-primary/15 text-foreground border border-primary/30"
                : "border border-transparent text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
            }`}
          >
            <div className="text-[12px] font-medium capitalize">
              {it.name.replace(/_/g, " ")}
            </div>
            <div className="text-[10px] text-muted-foreground font-mono truncate">
              {it.sub}
            </div>
          </button>
        ))}
      </div>
      <button
        onClick={onAdd}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded border border-dashed border-border text-[11px] text-muted-foreground hover:text-primary hover:border-primary/40 py-2"
      >
        <Plus className="h-3 w-3" /> {addLabel}
      </button>
    </div>
  );
}

function FieldLabel({
  label,
  help,
}: {
  label: string;
  help: string;
}) {
  return (
    <div className="flex items-center gap-1.5 mb-1.5">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="group relative">
        <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
        <span className="invisible group-hover:visible absolute left-5 top-0 z-50 w-[280px] rounded-md border border-border bg-popover px-2.5 py-2 text-[11px] text-foreground/90 shadow-xl pointer-events-none leading-relaxed">
          {help}
        </span>
      </span>
    </div>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-surface-elevated border border-border rounded-md px-2.5 py-1.5 text-[12px] outline-none focus:border-primary/50 ${
        mono ? "font-mono" : ""
      }`}
    />
  );
}

function TextArea({
  value,
  onChange,
  placeholder,
  rows = 2,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full bg-surface-elevated border border-border rounded-md px-2.5 py-1.5 text-[12px] outline-none focus:border-primary/50 resize-none leading-relaxed"
    />
  );
}

function MultiChipPicker({
  options,
  selected,
  onChange,
  emptyLabel = "Pick one or more",
}: {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  emptyLabel?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {options.length === 0 ? (
          <span className="text-[11px] text-muted-foreground italic">{emptyLabel}</span>
        ) : (
          options.map((opt) => {
            const active = selected.includes(opt);
            return (
              <button
                key={opt}
                onClick={() =>
                  onChange(
                    active ? selected.filter((s) => s !== opt) : [...selected, opt],
                  )
                }
                className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] transition ${
                  active
                    ? "border-primary/40 bg-primary/15 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {active && <Check className="h-2.5 w-2.5" />}
                <span className="capitalize">{opt.replace(/_/g, " ")}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------- Editors per kind ----------

function EntityEditor({
  value,
  onChange,
  onDelete,
  referencedBy,
}: {
  value: OntologyEntityType;
  onChange: (next: OntologyEntityType) => void;
  onDelete: () => void;
  referencedBy: string[];
}) {
  const [confirmDel, setConfirmDel] = useState(false);
  const attrs = Object.entries(value.attribute_schema);

  return (
    <div className="space-y-4 text-[12px]">
      <EditorHeader
        kind="entity"
        nameHelp="Snake case, lowercase. The chat will refer to this exact string when classifying nodes (e.g. type: 'counterparty'). Spaces become underscores."
      />

      <div>
        <FieldLabel
          label="Name"
          help="Snake case, lowercase. The chat will refer to this exact string when classifying nodes (e.g. type: 'counterparty')."
        />
        <TextInput
          mono
          value={value.name}
          onChange={(v) => onChange({ ...value, name: slugify(v) })}
          placeholder="e.g. counterparty"
        />
      </div>

      <div>
        <FieldLabel
          label="Description"
          help="What does this type mean in your domain? The AI model reads this when deciding whether a new node fits this type. Be specific."
        />
        <TextArea
          value={value.description ?? ""}
          onChange={(v) => onChange({ ...value, description: v })}
          placeholder="e.g. A firm or institution our portfolio carries credit exposure to."
        />
      </div>

      <div>
        <FieldLabel
          label="Example"
          help="One concrete instance of this type. Helps both you and the model anchor what's in vs out of scope."
        />
        <TextInput
          value={value.example ?? ""}
          onChange={(v) => onChange({ ...value, example: v })}
          placeholder="e.g. Acme Capital — BBB rated, US-based, $250M exposure"
        />
      </div>

      <div>
        <FieldLabel
          label="Attributes"
          help="Optional fields you want to track on every instance. The schema is permissive — ingestion can attach extra keys not listed here, but listing them tells the AI to look for them."
        />
        <div className="space-y-1.5">
          {attrs.length === 0 && (
            <div className="text-[11px] text-muted-foreground italic px-1">
              No attributes declared. Add one to teach the model what to extract.
            </div>
          )}
          {attrs.map(([k, t]) => (
            <div key={k} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center">
              <input
                value={k}
                onChange={(e) => {
                  const newName = slugify(e.target.value);
                  if (!newName || newName === k) return;
                  const next = { ...value.attribute_schema };
                  delete next[k];
                  next[newName] = t;
                  onChange({ ...value, attribute_schema: next });
                }}
                className="bg-surface-elevated border border-border rounded px-2 py-1 text-[11px] font-mono outline-none focus:border-primary/40"
              />
              <select
                value={String(t)}
                onChange={(e) =>
                  onChange({
                    ...value,
                    attribute_schema: { ...value.attribute_schema, [k]: e.target.value },
                  })
                }
                className="bg-surface-elevated border border-border rounded px-2 py-1 text-[11px] outline-none focus:border-primary/40"
              >
                {ATTR_TYPES.map((tt) => (
                  <option key={tt} value={tt}>
                    {tt}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const next = { ...value.attribute_schema };
                  delete next[k];
                  onChange({ ...value, attribute_schema: next });
                }}
                className="text-muted-foreground hover:text-risk-high p-1"
                title="Remove attribute"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <button
            onClick={() => {
              let n = "new_attribute";
              let i = 1;
              while (n in value.attribute_schema) {
                i++;
                n = `new_attribute_${i}`;
              }
              onChange({
                ...value,
                attribute_schema: { ...value.attribute_schema, [n]: "string" },
              });
            }}
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-primary"
          >
            <Plus className="h-3 w-3" /> Add attribute
          </button>
        </div>
      </div>

      <DeleteRow
        kind="entity type"
        confirm={confirmDel}
        setConfirm={setConfirmDel}
        onDelete={onDelete}
        referencedBy={referencedBy}
      />
    </div>
  );
}

function RelationshipEditor({
  value,
  entityTypes,
  onChange,
  onDelete,
}: {
  value: OntologyRelationshipType;
  entityTypes: string[];
  onChange: (next: OntologyRelationshipType) => void;
  onDelete: () => void;
}) {
  const [confirmDel, setConfirmDel] = useState(false);
  return (
    <div className="space-y-4 text-[12px]">
      <EditorHeader
        kind="relationship"
        nameHelp="Snake case verb phrase. The chat uses this string to label graph edges (e.g. 'counterparty_to', 'operates_in', 'allies_with')."
      />

      <div>
        <FieldLabel
          label="Name"
          help="Snake case verb phrase. The chat uses this exact string to label edges."
        />
        <TextInput
          mono
          value={value.name}
          onChange={(v) => onChange({ ...value, name: slugify(v) })}
          placeholder="e.g. counterparty_to"
        />
      </div>

      <div>
        <FieldLabel
          label="Description"
          help="What does this connection mean? When should the chat draw this edge vs another? Helps the model pick the right relationship name."
        />
        <TextArea
          value={value.description ?? ""}
          onChange={(v) => onChange({ ...value, description: v })}
          placeholder="e.g. Indicates a contractual obligation that can produce credit exposure between two parties."
        />
      </div>

      <div>
        <FieldLabel
          label="Example"
          help="One sentence showing the relationship in use, with both ends named. Anchors the model."
        />
        <TextInput
          value={value.example ?? ""}
          onChange={(v) => onChange({ ...value, example: v })}
          placeholder="e.g. Acme Capital → counterparty_to → GlobalBank"
        />
      </div>

      <div>
        <FieldLabel
          label="Allowed source types"
          help="The entity types that can appear on the left side of this relationship. Pick at least one. Picking more makes the relationship more permissive."
        />
        <MultiChipPicker
          options={entityTypes}
          selected={value.source_types}
          onChange={(next) =>
            onChange({ ...value, source_types: next.length ? next : value.source_types })
          }
        />
      </div>

      <div>
        <FieldLabel
          label="Allowed target types"
          help="The entity types that can appear on the right side. Combined with source types, this defines which edges are valid in the graph."
        />
        <MultiChipPicker
          options={entityTypes}
          selected={value.target_types}
          onChange={(next) =>
            onChange({ ...value, target_types: next.length ? next : value.target_types })
          }
        />
      </div>

      <DeleteRow
        kind="relationship"
        confirm={confirmDel}
        setConfirm={setConfirmDel}
        onDelete={onDelete}
      />
    </div>
  );
}

function SignalEditor({
  value,
  entityTypes,
  onChange,
  onDelete,
}: {
  value: OntologySignalType;
  entityTypes: string[];
  onChange: (next: OntologySignalType) => void;
  onDelete: () => void;
}) {
  const [confirmDel, setConfirmDel] = useState(false);
  return (
    <div className="space-y-4 text-[12px]">
      <EditorHeader
        kind="signal"
        nameHelp="Snake case. The chat uses this string when classifying time-stamped events (e.g. 'credit_rating_change', 'adverse_event')."
      />

      <div>
        <FieldLabel
          label="Name"
          help="Snake case. Used as the signal's type field on every event of this kind."
        />
        <TextInput
          mono
          value={value.name}
          onChange={(v) => onChange({ ...value, name: slugify(v) })}
          placeholder="e.g. credit_rating_change"
        />
      </div>

      <div>
        <FieldLabel
          label="Description"
          help="What kind of event is this? When should ingestion or chat label something with this type vs another? Helps disambiguate at the moment of classification."
        />
        <TextArea
          value={value.description ?? ""}
          onChange={(v) => onChange({ ...value, description: v })}
          placeholder="e.g. A rating agency adjusts a counterparty's credit rating up or down."
        />
      </div>

      <div>
        <FieldLabel
          label="Example"
          help="One realistic instance, written as the model would generate it."
        />
        <TextInput
          value={value.example ?? ""}
          onChange={(v) => onChange({ ...value, example: v })}
          placeholder="e.g. Moody's downgrades Acme Capital from BBB+ to BBB"
        />
      </div>

      <div>
        <FieldLabel
          label="Can be attached to"
          help="The entity types this signal can reference. The chat will reject a signal whose related entities aren't in this list. Picking more makes the signal more flexible."
        />
        <MultiChipPicker
          options={entityTypes}
          selected={value.related_entity_types}
          onChange={(next) => onChange({ ...value, related_entity_types: next })}
        />
      </div>

      <DeleteRow
        kind="signal type"
        confirm={confirmDel}
        setConfirm={setConfirmDel}
        onDelete={onDelete}
      />
    </div>
  );
}

function EditorHeader({
  kind,
  nameHelp: _nameHelp,
}: {
  kind: "entity" | "relationship" | "signal";
  nameHelp: string;
}) {
  const label =
    kind === "entity"
      ? "Editing entity type"
      : kind === "relationship"
        ? "Editing relationship type"
        : "Editing signal type";
  return (
    <div className="flex items-center gap-2 pb-3 border-b border-border">
      <Sparkles className="h-3.5 w-3.5 text-primary" />
      <Eyebrow>{label}</Eyebrow>
    </div>
  );
}

function DeleteRow({
  kind,
  confirm,
  setConfirm,
  onDelete,
  referencedBy,
}: {
  kind: string;
  confirm: boolean;
  setConfirm: (b: boolean) => void;
  onDelete: () => void;
  referencedBy?: string[];
}) {
  return (
    <div className="border-t border-border pt-3">
      {referencedBy && referencedBy.length > 0 && !confirm && (
        <div className="text-[11px] text-muted-foreground mb-2">
          Referenced by: {referencedBy.join(", ")}. Deleting will also strip those references.
        </div>
      )}
      {confirm ? (
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-risk-high flex-1">
            Delete this {kind}? Existing nodes of this type stay in the graph but
            new ones will be rejected.
          </span>
          <button
            onClick={() => setConfirm(false)}
            className="text-[11px] text-muted-foreground"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onDelete();
              setConfirm(false);
            }}
            className="inline-flex items-center gap-1.5 rounded bg-risk-high text-white px-2.5 py-1 text-[11px] font-semibold"
          >
            <Trash2 className="h-3 w-3" /> Delete
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirm(true)}
          className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-risk-high"
        >
          <Trash2 className="h-3 w-3" /> Delete this {kind}
        </button>
      )}
    </div>
  );
}

// ---------- helpers ----------

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function uniqueName(base: string, existing: string[]): string {
  let n = base;
  let i = 1;
  const set = new Set(existing);
  while (set.has(n)) {
    i++;
    n = `${base}_${i}`;
  }
  return n;
}
